import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, Search, Loader2 } from 'lucide-react'
import api from '../services/api.ts'
import { useAuth } from '../context/AuthContext.tsx'

interface PickerAgencyUser {
  role: string
  agencies: { id: string; name: string } | null
}

interface PickerUser {
  id: string
  name: string | null
  email: string
  is_system_admin: boolean | number
  agency_users: PickerAgencyUser[]
  companyAccessCount?: number
}

function accessLevel(u: PickerUser): { label: string; className: string } {
  if (u.is_system_admin) {
    return { label: 'System Admin', className: 'bg-purple-100 text-purple-700' }
  }
  const agencyAdmin = (u.agency_users || []).find(au => au.role === 'ADMIN')
  if (agencyAdmin) {
    return {
      label: `Agency Admin · ${agencyAdmin.agencies?.name || 'Agency'}`,
      className: 'bg-blue-100 text-blue-700',
    }
  }
  const agencyMember = (u.agency_users || [])[0]
  if (agencyMember) {
    return {
      label: `Agency Member · ${agencyMember.agencies?.name || 'Agency'}`,
      className: 'bg-sky-100 text-sky-700',
    }
  }
  if (u.companyAccessCount && u.companyAccessCount > 0) {
    return {
      label: `Company access (${u.companyAccessCount})`,
      className: 'bg-teal-100 text-teal-700',
    }
  }
  return { label: 'User', className: 'bg-gray-100 text-gray-600' }
}

/**
 * Eye-icon dropdown next to the Admin menu: lists all users with their access
 * level so a system admin can "log in as" any of them in one click.
 */
export default function ImpersonationPicker() {
  const { user: currentUser, impersonateUser } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useState<PickerUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null)

  useEffect(() => {
    if (!open || users.length > 0) return
    setLoading(true)
    setError(null)
    api
      .get('/api/admin/users')
      .then(res => setUsers(res.data.users || []))
      .catch(err => setError(err.response?.data?.message || 'Failed to load users'))
      .finally(() => setLoading(false))
  }, [open, users.length])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter(
      u => (u.name || '').toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    )
  }, [users, search])

  const handleImpersonate = async (userId: string) => {
    setImpersonatingId(userId)
    try {
      await impersonateUser(userId)
      setOpen(false)
      navigate('/')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to impersonate user')
    } finally {
      setImpersonatingId(null)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-gray-700 hover:text-primary-700 transition-colors"
        title="Log in as another user"
      >
        <Eye size={16} />
        <span className="sr-only">Log in as</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="p-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Log in as
              </p>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  autoFocus
                  placeholder="Search users..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto py-1">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                  <Loader2 size={16} className="animate-spin" /> Loading users…
                </div>
              ) : error ? (
                <div className="px-4 py-4 text-sm text-red-600">{error}</div>
              ) : filtered.length === 0 ? (
                <div className="px-4 py-4 text-sm text-gray-500">No users match your search</div>
              ) : (
                filtered.map(u => {
                  const level = accessLevel(u)
                  const isSelf = u.id === currentUser?.id
                  return (
                    <button
                      key={u.id}
                      disabled={isSelf || impersonatingId !== null}
                      onClick={() => handleImpersonate(u.id)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isSelf ? 'This is you' : `Log in as ${u.name || u.email}`}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">
                          {u.name || u.email}
                          {isSelf && <span className="text-gray-400 font-normal"> (you)</span>}
                        </span>
                        <span className="block text-xs text-gray-500 truncate">{u.email}</span>
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${level.className}`}
                        >
                          {level.label}
                        </span>
                        {impersonatingId === u.id && (
                          <Loader2 size={14} className="animate-spin text-gray-400" />
                        )}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
